import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import {
  AccordionDetails,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { sectionNames } from 'src/components/QueryMenus/checklistQueriesData';
import { QueryMenus } from 'src/components/QueryMenus/QueryMenus';
import { SectionQueryIndication } from 'src/components/QueryMenus/sectionQueryIndication';
import { useQueryMenus } from 'src/components/QueryMenus/useQueryMenus';
import { ViewQuery } from 'src/components/QueryMenus/ViewQuery/ViewQuery';
import { sendRequest } from 'src/hooks/useFetch';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
// import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { clearMessage, setMessage } from 'src/store/Message';
import { ISIVReportSourceDocumentation } from 'src/types/SIVReport';
import { formatInput } from 'src/util/STR';
import { useFieldTranslation } from '../../fields/useFieldTranslation';
import {
  ISIVReportMandatory,
  ISIVReportModifiedKeys,
  ISIVReportModifiedKeysOfKeys,
  ISourceDocumentationMandatory,
  MandatoryData
} from './useSivReport';
import { setIsSaveButtonDisable } from 'src/store/sivReport';

const NOTES_MAXLENGTH = 2000;

export interface ISourceDocumentation {
  getSivReportData: (key: ISIVReportModifiedKeysOfKeys, add: boolean) => void;
  saveDisable: boolean;
  updateSourceDocumentation: (data: ISIVReportSourceDocumentation) => void;
  sourceDocumentationData: ISIVReportSourceDocumentation;
  isUpdate: boolean;
  isChange: boolean;
  setIsChange: (step: ISIVReportModifiedKeys, key: ISIVReportModifiedKeysOfKeys, val: boolean) => void;
  updateSIVReportMandatoryData: (key: keyof ISIVReportMandatory, value: MandatoryData) => void;
  mandatory: ISourceDocumentationMandatory;
  sivReportSavedId: string;
  checkedInStartDate: any;
  isPublished: boolean;
  SignatureBreaking: () => void;
}

const SourceDocumentation: React.FunctionComponent<ISourceDocumentation> = ({
  getSivReportData,
  saveDisable,
  sourceDocumentationData,
  updateSourceDocumentation,
  isUpdate,
  isChange,
  setIsChange,
  mandatory,
  updateSIVReportMandatoryData,
  sivReportSavedId,
  checkedInStartDate,
  isPublished,
  SignatureBreaking
}) => {
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const user = useUser();
  const { sivcDataUpdateSuccessMsg, sivcMendatoryInfoErrorMsg, reportSourceDocumentation } = useFieldTranslation();

  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_siv_report');
  const perDisableQuery = checkPermission('view_queries_siv_report');
  const selectedRowData: any = useAppSelector(state => state.sivReport.selectedRowData);
  const saveButtonDisable: any = useAppSelector(state => state.sivReport.disableSaveButtonProps);
  const [refreshKey, setRefreshKey] = useState(0);
  const errorText = 'Required';

  useEffect(() => {
    setRefreshKey(refreshKey + 1);
  }, [sourceDocumentationData]);

  const handleSourceDocumentationData = (e: any) => {
    dispatchApp(clearMessage());
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    const value = formattedVal;
    if (e.target.type === 'radio' && value === 'Y') {
      updateSourceDocumentation({ ...sourceDocumentationData, [e.target.name]: value });

      updateSIVReportMandatoryData('sourceDocumentation', { ...mandatory, [e.target.name]: false });
    } else {
      updateSourceDocumentation({ ...sourceDocumentationData, [e.target.name]: value });
      updateSIVReportMandatoryData('sourceDocumentation', { ...mandatory, [e.target.name]: false });
    }
    if (saveButtonDisable && e.target.type === 'radio') {
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, sourceDocumentation: false }));
    }
    setConfirmExitValue(dispatch, true);
  };

  const handleChange = () => {
    if (saveButtonDisable) {
      dispatch(setIsSaveButtonDisable(false));
    }
  };

  const setValidationData = () => {
    const copyValidation = { ...mandatory };
    for (const key of Object.keys(copyValidation)) {
      if (
        sourceDocumentationData[key as keyof ISIVReportSourceDocumentation] === 'N' &&
        !(
          sourceDocumentationData[`${key}_comm` as keyof ISIVReportSourceDocumentation] &&
          sourceDocumentationData[`${key}_comm` as keyof ISIVReportSourceDocumentation] !== null &&
          sourceDocumentationData[`${key}_comm` as keyof ISIVReportSourceDocumentation] !== ''
        )
      ) {
        copyValidation[key as keyof ISourceDocumentationMandatory] = true;
      } else {
        copyValidation[key as keyof ISourceDocumentationMandatory] = false;
      }
    }
    updateSIVReportMandatoryData('sourceDocumentation', copyValidation);
    return copyValidation;
  };
  const updateSourceDocumentationData = async () => {
    const validate = setValidationData();
    if (Object.values(validate).indexOf(true) !== -1) {
      return dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
    const options = {
      site_id: selectedSite,
      study_id: studyId,
      data: {
        updated_by: user.user,
        monitoring_visit_id: selectedRowData?.id,
        siv_report: { step_two: { source_documentation: sourceDocumentationData } }
      },
      pathVariables: {
        country: selectedCountry,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      if (isChange) {
        if (isUpdate) {
          if (
            sourceDocumentationData.siv_report_source_doc_review !== null &&
            sourceDocumentationData.siv_report_source_doc_review_comm !== null
          ) {
            await sendRequest('update_siv_report', options);
            if (isPublished) {
              SignatureBreaking();
            }
            getSivReportData('sourceDocumentation', false);
            setConfirmExitValue(dispatch, false);
            setIsChange('Step2', 'sourceDocumentation', false);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
        if (!isUpdate) {
          if (
            sourceDocumentationData.siv_report_source_doc_review !== null &&
            sourceDocumentationData.siv_report_source_doc_review_comm !== null
          ) {
            await sendRequest('create_siv_report', options);
            getSivReportData('sourceDocumentation', true);
            setIsChange('Step2', 'sourceDocumentation', false);
            setConfirmExitValue(dispatch, false);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
      } else if (!isChange && sourceDocumentationData.siv_report_source_doc_review === null) {
        dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
      } else {
        dispatchApp(setMessage({ msgDetails: 'No data modified', severity: 'info' }));
      }
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, sourceDocumentation: true }));
    } catch (e) {
      dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
  };

  /**
   * Query menus code start
   */

  const {
    contextMenu,
    setContextMenu,
    queryKeys,
    setQueryKeys,
    checkListQueries,
    raisedQueries,
    totalFieldQueries,
    totalSectionQueries,
    fieldQueries,
    getSivReportQueries,
    report_id: reportId,
    setReport_id: setReportId,
    queryType,
    setQueryType,
    raisedSectionQueries,
    latestSecQueryRaisedBy
  } = useQueryMenus('SIV REPORT');

  useEffect(() => {
    setReportId(sivReportSavedId);
  }, [sivReportSavedId]);

  const handleContextMenu = (event: React.MouseEvent, checklistItem: keyof ISIVReportSourceDocumentation) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null &&
        (checkPermission('view_queries_siv_report') ||
          checkPermission('view_audit_siv_report') ||
          checkPermission('raise_query_siv_report'))
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4
          }
        : null
    );
    const sectionFieldNames: string[] = [];
    Object.keys(checkListQueries.informed_consent).forEach((obj: any) => {
      sectionFieldNames.push(checkListQueries.informed_consent[obj].report_field_name);
    });
    const newObj = {
      ...checkListQueries.source_documentation[checklistItem],
      id: sivReportSavedId,
      section_key: 'source_documentation',
      report_field_value: sourceDocumentationData[checklistItem]
    };
    setQueryKeys(newObj);
  };
  const [openViewQuery, setOpenViewQuery] = useState(false);
  const viewNotes = (checklistItem: keyof ISIVReportSourceDocumentation, isAllFieldQueries = false) => {
    let newObj: any = {};
    if (isAllFieldQueries) {
      const sectionFieldNames: string[] = [];
      Object.keys(checkListQueries.source_documentation).forEach((obj: any) => {
        sectionFieldNames.push(checkListQueries.source_documentation[obj].report_field_name);
      });
      newObj = {
        ...checkListQueries.source_documentation[checklistItem],
        id: sivReportSavedId,
        section_key: 'source_documentation',
        section_field_names: sectionFieldNames,
        is_all_field_queries: isAllFieldQueries
      };
    } else {
      newObj = {
        ...checkListQueries.source_documentation[checklistItem],
        id: sivReportSavedId,
        section_key: 'source_documentation',
        report_field_value: sourceDocumentationData[checklistItem],
        is_all_field_queries: isAllFieldQueries
      };
    }

    setQueryKeys(newObj);
    setOpenViewQuery(true);
    setQueryType('ALL');
  };

  /**
   * Query menus code end
   */
  return (
    <>
      <AccordionDetails className="sq-checklist-tab protocol-compliance">
        <Grid container className="checklist-radio-actions">
          {raisedSectionQueries.includes(sectionNames.source_documentation) &&
            sectionNames.source_documentation in latestSecQueryRaisedBy &&
            SectionQueryIndication(
              latestSecQueryRaisedBy,
              sectionNames.source_documentation,
              viewNotes,
              'siv_report_source_doc_review'
            )}
          <Grid md={12}>
            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_source_doc_review');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9}>
                {reportSourceDocumentation.label1}. <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_source_doc_review"
                      value={sourceDocumentationData.siv_report_source_doc_review}
                      onChange={handleSourceDocumentationData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_source_doc_review')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${
                      raisedQueries.includes('siv_report_source_doc_review') ||
                      raisedSectionQueries.includes(sectionNames.source_documentation)
                        ? ' danger-link'
                        : ''
                    }`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input">
                  <TextField
                    name="siv_report_source_doc_review_comm"
                    key={`siv_report_source_doc_review_comm-${refreshKey}`}
                    defaultValue={sourceDocumentationData.siv_report_source_doc_review_comm || ''}
                    onBlur={handleSourceDocumentationData}
                    error={mandatory['siv_report_source_doc_review']}
                    helperText={mandatory['siv_report_source_doc_review'] ? errorText : ''}
                    label="Notes"
                    variant="outlined"
                    inputProps={{ maxLength: NOTES_MAXLENGTH }}
                    disabled={!isDisable || saveDisable || !checkedInStartDate}
                    onChange={handleChange}
                    multiline
                    minRows={1}
                    maxRows={10}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Grid container className="fixed-buttons-block">
        {checkPermission('update_siv_report') ? (
          <Grid md={12} display="flex" justifyContent="flex-end">
            <Stack spacing={2} direction="row">
              {isDisable ? (
                <Button
                  onClick={updateSourceDocumentationData}
                  variant="text"
                  disabled={saveButtonDisable?.sourceDocumentation || !checkedInStartDate}
                >
                  SAVE
                </Button>
              ) : (
                ''
              )}
            </Stack>
          </Grid>
        ) : (
          ''
        )}
        <QueryMenus
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          queryKeys={queryKeys}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          report_id={reportId}
          checkListQueries={checkListQueries}
          queryType={queryType}
          setQueryType={setQueryType}
          isSQSivQueries
        />
        <ViewQuery
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          open={openViewQuery}
          setOpen={setOpenViewQuery}
          queryKeys={queryKeys}
          queryType={queryType}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          setQueryType={setQueryType}
          report_id={reportId}
          isSQSivQueries
        />
      </Grid>
    </>
  );
};

export default SourceDocumentation;
